import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {PlusOutlined} from '@ant-design/icons';
import {Button, Typography} from 'antd';
import {FormShell} from 'components/form/FormShell';
import {TextInput} from 'components/form/TextInput';
import {usePrompt} from 'helpers/useBlocker';
import {observer} from 'mobx-react-lite';
import {appState} from 'store/AppStore';
import {VehicleState} from 'store/vehicle/VehicleState';
import {GarageSpotAttributeDefinitionDto, GarageSpotAttributeDto} from '../../../../../../PRMGApiClient';
import {GarageSpotAttributeSelectionModal} from '../utils/GarageSpotAttributeSelectionModal';
import {VehicleEditKey} from '../VehicleEditView';

interface VehicleParkingSpotAttributesFormProps {
  state: VehicleState;
}

interface GarageSpotAttribute extends Omit<GarageSpotAttributeDto, 'attributeDefinitionId'> {
  attributeDefinition: GarageSpotAttributeDefinitionDto;
}

export const VehicleParkingSpotAttributesForm: React.FC<VehicleParkingSpotAttributesFormProps> = observer(
  ({state}): JSX.Element => {
    const {t} = useTranslation();

    const [openModal, setOpenModal] = useState(false);

    const {attributeDefinitions} = appState;
    const garageSpot = state.item.garageSpot;

    const {
      register,
      handleSubmit,
      reset,
      formState: {isDirty},
    } = useForm<{attributes: Array<GarageSpotAttributeDto>}>();

    useEffect(() => {
      state.setDirty(VehicleEditKey.parkingSpotAttributes, isDirty);
    }, [isDirty]);

    // Find attribute values for this garage spot and sort them by attribute definition id
    const attributeValues: Array<GarageSpotAttribute> = useMemo(() => {
      return (
        garageSpot?.attributeValues
          ?.map((attribute) => {
            const definition = attributeDefinitions.find(
              (d) => d.id === attribute.attributeDefinitionId
            ) as GarageSpotAttributeDefinitionDto;
            return {...attribute, attributeDefinition: definition};
          })
          .sort((a, b) => (a.attributeDefinitionId ?? 0) - (b.attributeDefinitionId ?? 0)) ?? []
      );
    }, [garageSpot, attributeDefinitions]);

    useEffect(() => {
      if (garageSpot?.id) {
        // Set default values for attributes
        reset({attributes: attributeValues});
      }
    }, [garageSpot?.id, attributeValues, reset]);

    usePrompt(t('Are you sure? There are unsaved changes.'), isDirty);

    return (
      <FormShell
        isDirty={isDirty}
        onReset={() => reset()}
        onSubmit={handleSubmit((data) => state.saveAttributes(data.attributes))}
        isLoading={state.isLoading}
      >
        {attributeValues.length > 0 ? (
          <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
            {attributeValues.map((attribute, idx) => (
              <TextInput
                key={attribute.id}
                placeholder={t('Enter attribute value')}
                label={attribute.attributeDefinition.name ?? ''}
                {...register(`attributes.${idx}.value`)}
              />
            ))}
          </div>
        ) : (
          <Typography.Text italic>{t('No attributes defined for this spot.')}</Typography.Text>
        )}
        <div className="block">
          <Button
            size="large"
            className="!p-0 mt-4"
            type="link"
            icon={<PlusOutlined />}
            onClick={() => setOpenModal(true)}
          >
            {t('Select attributes')}
          </Button>
        </div>
        <GarageSpotAttributeSelectionModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          state={state}
          attributeValues={attributeValues}
        />
      </FormShell>
    );
  }
);
